/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {memo, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useSearchParams} from 'react-router-dom'
import {useAuth} from '../core/Auth'
import {useIntl} from 'react-intl'
import api from '../../../api'
import {useSetupContext} from '../../../constructor/helpers/SetupContext'
import { normalizeError } from '../utils/authApi'

enum Step {
  Email,
  Code,
  Password,
}

const initialValues = {
  email: '',
  password: '',
  confirm_password: '',
}

export const EmailPasswordReset = memo(() => {

  const [step, setStep] = useState<Step>(Step.Email)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')

  const {context} = useSetupContext()
  const haveRegistration = Boolean(context.sign_up)
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendCode = async () => {
      setResendError(null);
      try {
          await api('users', 'send-reset-code', { email });
      } catch (e) {
          console.error(e);
          setResendError(intl.formatMessage({ id: 'AUTH.FAILED_RESEND_CODE' }));
      }
  }

  // Скрыть/Показать пароль или код (глазик)
  const [visibility, setVisibility] = useState({
      password: false,
      confirmPassword: false,
      code: false,
  })
  const toggleVisibility = (field: keyof typeof visibility) => {
      setVisibility(prev => ({
          ...prev,
          [field]: !prev[field],
      }))
  }

    const getAuthErrorKey = (code: string) => {
        const serverKey = `AUTH.SERVER.${code}`
        const baseKey = `AUTH.${code}`

        return intl.messages[serverKey] ? serverKey : baseKey
    }

  const formEmail = useFormik({
    initialValues: {email: ''},
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(intl.formatMessage({ id: 'AUTH.REGISTRATION_EMAIL_VALIDATION_FORMAT' }))
        .required(intl.formatMessage({ id: 'AUTH.REGISTRATION_EMAIl_VALIDATION_REQUIRED' })),
    }),
    onSubmit: async ({email}, { setStatus }) => {
      setLoading(true)
      try {
        await api('users', 'send-reset-code', {email}, {preserveError: true})
        setEmail(email)
        setStep(Step.Code)
      } catch (error: any) {
        console.log(error)
          const err = normalizeError(error)
          setStatus(
              err.code
                  ? intl.formatMessage({
                      id: getAuthErrorKey(err.code),
                      defaultMessage: err.message,
                  })
                  : err.message
          )
      } finally {
        setLoading(false)
      }
    },
  })

  const formCode = useFormik({
    initialValues: {code: ''},
    validationSchema: Yup.object().shape({
      code: Yup.string().required(intl.formatMessage({ id: 'AUTH.CODE_REQUIRED' })),
    }),
    onSubmit: async ({code}, { setStatus }) => {
      setLoading(true)
      try {
        await api('users', 'check-code', {email, code}, {preserveError: true})
        setCode(code)
        setStep(Step.Password)
      } catch (error: any) {
        const err = normalizeError(error)
        setStatus(
            err.code
                ? intl.formatMessage({
                    id: getAuthErrorKey(err.code),
                    defaultMessage: err.message,
                })
                : err.message
        )
      } finally {
        setLoading(false)
      }
    },
  })

  const formPassword = useFormik({
    initialValues: {
      password: '',
      confirm_password: '',
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .min(8, intl.formatMessage({ id: 'AUTH.REGISTRATION_PASSWORD_VALIDATION_COMBINATION' }))
        .matches(/[a-z]/, intl.formatMessage({ id: 'AUTH.REGISTRATION_PASSWORD_VALIDATION_COMBINATION' }))
        .matches(/[A-Z]/, intl.formatMessage({ id: 'AUTH.REGISTRATION_PASSWORD_VALIDATION_COMBINATION' }))
        .matches(/[0-9]/, intl.formatMessage({ id: 'AUTH.REGISTRATION_PASSWORD_VALIDATION_COMBINATION' }))
        .required(intl.formatMessage({ id: 'AUTH.REGISTRATION_PASSWORD_VALIDATION_REQUIRED' })),
      confirm_password: Yup.string()
        .oneOf([Yup.ref('password')], intl.formatMessage({ id: 'AUTH.REGISTRATION_PASSWORD_VALIDATION_MATCH' }))
        .required(intl.formatMessage({ id: 'AUTH.REGISTRATION_CONFIRM_PASSWORD_VALIDATION_REQUIRED' })),
    }),
    onSubmit: async ({password}, { setStatus }) => {
      setLoading(true)
      try {
        await api('users', 'forgot-password', {email, code, password}, {preserveError: true})
        setSuccess(true)
      } catch (errors: any) {
         const err = normalizeError(errors)
         setStatus(
              err.code
                  ? intl.formatMessage({
                      id: getAuthErrorKey(err.code),
                      defaultMessage: err.message,
                  })
                  : err.message
         )
        saveAuth(undefined)
      } finally {
        setLoading(false)
      }
    },
  })

  if (!haveRegistration){
    return <div className="auth_form">
      <div className='text-center mb-8'>
        <h1 className='auth_formTitle'>{intl.formatMessage({ id: "AUTH.REGISTRATION_CLOSED_REGISTRATION_TITLE" })}</h1>
        <div className='auth_formDescription'>{intl.formatMessage({ id: "AUTH.REGISTRATION_CLOSED_REGISTRATION_DESCRIPTION" })}</div>
      </div>
      <div className='d-grid mb-5'>
        <Link to='/auth'>
          <button
            type='button'
            id='kt_login_signup_form_cancel_button'
            className='auth_button btn btn-secondary w-100'
          >
            {intl.formatMessage({ id: "BUTTON.PREVIOUS" })}
          </button>
        </Link>
      </div>
    </div>
  }

  if (success) {
    return (
      <div className='auth_form'>
        <div className='text-center mb-8'>
          <h1 className='auth_formTitle'>
            {intl.formatMessage({id: 'AUTH.RESET_PASSWORD_SUCCESS_TITLE'})}
          </h1>
        </div>
        <div className='d-grid mb-5'>
          <Link to='/auth'>
            <button
              type='button'
              id='kt_login_signup_form_cancel_button'
              className='auth_button btn btn-secondary w-100'
            >
              {intl.formatMessage({id: 'BUTTON.PREVIOUS'})}
            </button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <form
      className='auth_form form w-100'
      noValidate
      id='kt_login_signup_form'
      onSubmit={
        step === Step.Email
          ? formEmail.handleSubmit
          : step === Step.Code
            ? formCode.handleSubmit
            : formPassword.handleSubmit
      }
    >

      {/* begin::Form group Email */}
      {step === Step.Email && (
        <>
          <div className='text-center mb-8'>
            <h1 className='auth_formTitle'>{intl.formatMessage({id: 'AUTH.RESET_PASSWORD_EMAIL_TITLE'})}</h1>
            <div className='auth_formDescription'>
              {intl.formatMessage({id: 'AUTH.RESET_PASSWORD_DESCRIPTION'})}
            </div>
          </div>
          <div className='fv-row mb-8'>
            <label className='auth_fieldLabel form-label'>
              {intl.formatMessage({id: 'AUTH.EMAIL_TITLE'})}
            </label>
            <input
              placeholder='Email'
              type='email'
              autoComplete='off'
              {...formEmail.getFieldProps('email')}
              className={clsx(
                'auth_field form-control bg-transparent',
                {'is-invalid': formEmail.touched.email && formEmail.errors.email},
                {
                  'is-valid': formEmail.touched.email && !formEmail.errors.email,
                }
              )}
            />
            {formEmail.touched.email && formEmail.errors.email && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formEmail.errors.email}</span>
                </div>
              </div>
            )}
            {formEmail.status ? (
              <div className='mt-5 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formEmail.status}</div>
              </div>
            ) : null}
          </div>
        </>
      )}

      {step === Step.Code && (
        <>
          <div className='text-center mb-8'>
            <h1 className='auth_formTitle'>{intl.formatMessage({id: 'AUTH.RESET_PASSWORD_EMAIL_TITLE'})}</h1>
            <div className='auth_formDescription'>
              {intl.formatMessage({id: 'AUTH.REGISTRATION_CODE_DESCRIPTION'})}
            </div>
          </div>
          <div className='fv-row mb-8'>
            <div className='d-flex align-items-center justify-content-between mb-1'>
              <label className='auth_fieldLabel form-label'>
                  {intl.formatMessage({id: 'AUTH.CODE_TITLE'})}
              </label>
              <button
                  type='button'
                  className='btn btn-sm btn-icon btn-eye'
                  onClick={() => toggleVisibility('code')}
                  aria-label={visibility.code ? 'Скрыть код' : 'Показать код'}
              >
                  <i className={visibility.code ? 'bi bi-eye-slash' : 'bi bi-eye'} style={{ fontSize: '1.25rem', color: 'white' }} />
              </button>
            </div>
            <input
              type={visibility.code ? 'text' : 'password'}
              autoComplete='off'
              {...formCode.getFieldProps('code')}
              className={clsx(
                'auth_field form-control bg-transparent',
                {'is-invalid': formCode.touched.code && formCode.errors.code},
                {
                  'is-valid': formCode.touched.code && !formCode.errors.code,
                }
              )}
            />
            {formCode.touched.code && formCode.errors.code && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formCode.errors.code}</span>
                </div>
              </div>
            )}
            {formCode.status ? (
              <div className='mt-5 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formCode.status}</div>
              </div>
            ) : null}
            {resendError && (
               <div className='mt-5 alert alert-danger'>
                   <div className='alert-text font-weight-bold'>{resendError}</div>
               </div>
            )}
          </div>
        </>
      )}

      {step === Step.Password && (
        <>
          <div className='text-center mb-8'>
            <h1 className='auth_formTitle'>{intl.formatMessage({id: 'AUTH.RESET_PASSWORD_EMAIL_TITLE'})}</h1>
            <div className='auth_formDescription'>
              {intl.formatMessage({id: 'AUTH.RESET_PASSWORD_EMAIL_NEW_PASSWORD_DESCRIPTION'})}
            </div>
          </div>
          {/* begin::Form group Password */}
          <div className='fv-row mb-8' data-kt-password-meter='true'>
            <div className='mb-1'>
              <div className='d-flex align-items-center justify-content-between mb-1'>
                  <label className='auth_fieldLabel form-label'>
                      {intl.formatMessage({id: 'AUTH.PASSWORD_TITLE'})}
                  </label>
                  <button
                      type='button'
                      className='btn btn-sm btn-icon btn-eye'
                      onClick={() => toggleVisibility('password')}
                      aria-label={visibility.password ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                      <i className={visibility.password ? 'bi bi-eye-slash' : 'bi bi-eye'} style={{ fontSize: '1.25rem', color: 'white' }} />
                  </button>
              </div>

              <div className='position-relative mb-3'>
                <input
                  type={visibility.password ? 'text' : 'password'}
                  autoComplete='off'
                  {...formPassword.getFieldProps('password')}
                  className={clsx(
                    'auth_field form-control bg-transparent',
                    {
                      'is-invalid': formPassword.touched.password && formPassword.errors.password,
                    },
                    {
                      'is-valid': formPassword.touched.password && !formPassword.errors.password,
                    }
                  )}
                />
                {formPassword.touched.password && formPassword.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formPassword.errors.password}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* end::Form group */}

          {/* begin::Form group Confirm password */}
          <div className='fv-row mb-5'>
            <div className='d-flex align-items-center justify-content-between mb-1'>
               <label className='auth_fieldLabel form-label'>
                   {intl.formatMessage({id: 'AUTH.CONFIRM_PASSWORD_TITLE'})}
               </label>
               <button
                   type='button'
                   className='btn btn-sm btn-icon btn-eye'
                   onClick={() => toggleVisibility('confirmPassword')}
                   aria-label={visibility.confirmPassword ? 'Скрыть пароль' : 'Показать пароль'}
               >
                   <i className={visibility.confirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} style={{ fontSize: '1.25rem', color: 'white' }} />
               </button>
            </div>
            <input
              type={visibility.confirmPassword ? 'text' : 'password'}
              autoComplete='off'
              {...formPassword.getFieldProps('confirm_password')}
              className={clsx(
                'auth_field form-control bg-transparent',
                {
                  'is-invalid': formPassword.touched.confirm_password && formPassword.errors.confirm_password,
                },
                {
                  'is-valid': formPassword.touched.confirm_password && !formPassword.errors.confirm_password,
                }
              )}
            />
            {formPassword.touched.confirm_password && formPassword.errors.confirm_password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formPassword.errors.confirm_password}</span>
                </div>
              </div>
            )}
          </div>

          {formPassword.status ? (
            <div className='mt-5 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formPassword.status}</div>
            </div>
          ) : null}
        </>
      )}
      {/* end::Form group */}

      <div className='d-flex flex-stack flex-wrap gap-2 fs-base fw-semibold mb-8' />

      {/* begin::Form group */}
      {/* begin::Action */}
      <div className='d-grid mb-5'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='auth_button btn btn-primary mb-5'
          disabled={loading}
        >
          {!loading && (
            <span className='indicator-label'>{intl.formatMessage({id: 'BUTTON.SUBMIT'})}</span>
          )}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {intl.formatMessage({id: 'AUTH.LOGIN_SUBMIT_BUTTON'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
          {step === Step.Code && (
              <button
                  type='button'
                  className='auth_button btn btn-secondary mb-5'
                  onClick={() => {
                      if (!email || resendDisabled) return;
                      setResendDisabled(true);
                      setResendTimer(30);
                      const interval = setInterval(() => {
                          setResendTimer(prev => {
                              if (prev <= 1) {
                                  clearInterval(interval);
                                  setResendDisabled(false);
                                  return 30;
                              }
                              return prev - 1;
                          });}, 1000);
                      void resendCode();
                  }}
                  disabled={resendDisabled || loading || !email}
              >
                  {resendDisabled
                      ? `${intl.formatMessage({id: 'AUTH.RESEND_CODE'})} (${resendTimer})`
                      : intl.formatMessage({id: 'AUTH.RESEND_CODE'})}
              </button>
          )}
        <Link to='/auth'>
          <button
            type='button'
            id='kt_login_signup_form_cancel_button'
            className='auth_button btn btn-secondary w-100'
          >
            {intl.formatMessage({id: 'BUTTON.CANCEL'})}
          </button>
        </Link>
      </div>
      {/* end::Action */}
      {/* end::Form group */}
    </form>
  )
})
